import clsx from 'clsx';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify';
import { scrollFieldIntoView } from '../../../../utils/scrollFieldIntoView';
import { Faq } from './FaqTab';

interface FaqItemProps {
  faq: Faq;
  onChange: (faq: Faq) => void;
  onDelete: (id: string) => void;
  onUnsavedChanges?: (id: string, hasChanges: boolean) => void;
  shouldHighlight?: boolean;
}

const FaqItem: React.FC<FaqItemProps> = memo(
  ({ faq, onChange, onDelete, onUnsavedChanges, shouldHighlight }) => {
    const { t } = useTranslation();
    const [localQuestion, setLocalQuestion] = useState(faq.question);
    const [localAnswer, setLocalAnswer] = useState(faq.answer);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
      setLocalQuestion(faq.question);
      setLocalAnswer(faq.answer);
      setHasChanges(false);
    }, [faq.question, faq.answer]);

    const onQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalQuestion(e.target.value);
      const newHasChanges =
        e.target.value !== faq.question || localAnswer !== faq.answer;
      setHasChanges(newHasChanges);
      onUnsavedChanges?.(faq.id, newHasChanges);
    };

    const onAnswerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalAnswer(e.target.value);
      const newHasChanges =
        e.target.value !== faq.answer || localQuestion !== faq.question;
      setHasChanges(newHasChanges);
      onUnsavedChanges?.(faq.id, newHasChanges);
    };

    const handleSave = () => {
      onChange({ ...faq, question: localQuestion, answer: localAnswer });
      setHasChanges(false);
      onUnsavedChanges?.(faq.id, false);
      toast.success(t('twin.faqSaved'));
    };

    const handleDelete = () => {
      onDelete(faq.id);
      toast.success(t('twin.faqDeleted'));
    };

    return (
      <div
        onFocus={scrollFieldIntoView}
        className={clsx(
          'rounded-2xl tc-mv-blur p-4 w-full mb-4 h-fit',
          shouldHighlight && 'border-2 !border-red-500',
        )}>
        <div className="flex items-start">
          <div className="flex-1 flex flex-col">
            <label htmlFor="question" className="block px-4 text-xs">
              {t('twin.userQuestion')}
            </label>
            <input
              type="text"
              className="px-4 bg-transparent text-white rounded-none w-full h-10 border-b border-white/15 outline-0"
              value={localQuestion}
              onChange={onQuestionChange}
              placeholder={t('twin.enterQuestion')}
            />
            <label htmlFor="answer" className="block px-4 text-xs mt-2">
              {t('twin.twinAnswer')}
            </label>
            <textarea
              className="px-4 bg-transparent text-white w-full rounded-none resize-none outline-none border-none p-1 min-h-12"
              style={
                {
                  fieldSizing: 'content',
                } as React.CSSProperties
              }
              value={localAnswer}
              onChange={onAnswerChange}
              placeholder={t('twin.enterAnswer')}
            />
          </div>
          <button type="button" onClick={handleDelete} className="w-10 h-10">
            <i className="fa-solid fa-trash" />
          </button>
        </div>

        <div className="flex gap-2 mt-2 justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges}
            className="btn btn-primary btn-sm flex-1">
            {t('twin.save')}
          </button>
        </div>
      </div>
    );
  },
);

export { FaqItem };
